import {useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useFormContext} from 'react-hook-form';
import * as Sentry from '@sentry/react';
import {usePreloadLeaderData} from './usePreloadLeaderData';
import {useSummary} from '../../context/summary';
import {useOpenModals} from 'context/openModals';
import {useGetGuestId} from '../../hooks/useGetGuestId';
import {useValidatePreloadedFields} from './useValidatePreloadedFields';
import {useDefaultContactData} from 'hooks/iv/useDefaultContactData';
import {
  useGuestFormFields,
  useGuest,
  usePreloadGuestData,
  useAsyncModalControls,
  usePath,
} from 'hooks';
import {PATHS} from 'routes/constants';
import {GUEST_FORM_NAMES} from '@chekin/core';
import {FormTypes} from '../../utils/guestForm';
import ChekinProSettings from '../../ChekinProSettings';
import AutoFillModalSheet from '../ModalSheets/AutoFill';
import {FeedbackModal, shouldShowFeedbackModalToUser} from 'components/FeedbackModal';
import {PrintingCardsModal} from 'components/Kiosko';
import {cn, useModalControls} from '@chekin/guestapp-ui-kit';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {VirtualKeyboardProvider} from './VirtualKeyboard';
import {
  Fields,
  FormWrapper,
  LoadingContainer,
  LoadingText,
  MainStyledLoader,
  StyledAutoCompleteGuestDataModalSheet,
} from './styled';

type GuestFormFieldsProps = {
  setErrorModal: (value: boolean) => void;
};

function GuestFormFieldsWrapper({setErrorModal}: GuestFormFieldsProps) {
  const layoutMode = useLayoutMode();

  if (layoutMode === 'kiosko') {
    return (
      <VirtualKeyboardProvider>
        <GuestFormFields setErrorModal={setErrorModal} />
      </VirtualKeyboardProvider>
    );
  }

  return <GuestFormFields setErrorModal={setErrorModal} />;
}

function GuestFormFields({setErrorModal}: GuestFormFieldsProps) {
  const {reservationId} = useSummary();
  const {t} = useTranslation();
  const guestId = useGetGuestId();
  const {guest, isLoading: isGuestLoading} = useGuest({id: guestId});
  const {countryCode} = useSummary();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const layoutMode = useLayoutMode();
  const {
    isOpen: isFeedbackModalOpen,
    openModal: openFeedbackModal,
    closeModal: closeFeedbackModal,
  } = useAsyncModalControls<void>();

  const {
    isLoadingForm,
    isLoadedForm,
    FormModals,
    renderFormFields,
    hasSignature,
    SignatureField,
    isDataIncompleteModalOpen,
  } = useGuestFormFields({
    guest,
    housingCountry: countryCode,
    onGuestRegistered: async data => {
      if (
        !ChekinProSettings.isSDK &&
        layoutMode == 'default' &&
        shouldShowFeedbackModalToUser(data)
      ) {
        await openFeedbackModal().catch(error => {
          Sentry.captureException(error);
          console.warn('Feedback modal error:', error);
        });
      }
    },
  });
  const {getValues, setValue} = useFormContext<FormTypes>();
  const {setIsFormTouched} = useOpenModals();
  const {
    isOpen: isAutoFillModalOpen,
    openModal: openAutoFillModal,
    closeModal: closeAutoFillModal,
  } = useModalControls();

  const {preloadPersistedFormData} = usePreloadGuestData({
    onPreloaded: openAutoFillModal,
    guestData: guest,
  });

  const {phone, isLeaderGuest} = useDefaultContactData();

  useEffect(() => {
    if (isLeaderGuest) {
      const values = getValues();
      if (phone && !values[GUEST_FORM_NAMES.phone]) {
        setValue(GUEST_FORM_NAMES.phone, phone);
      }
    }
  }, [getValues, setValue, phone, isLeaderGuest]);

  useValidatePreloadedFields();

  usePreloadLeaderData({countryCode});

  useEffect(() => {
    setErrorModal(isDataIncompleteModalOpen);
    if (isLoadedForm) {
      preloadPersistedFormData(guestId);
      setIsFormTouched(Boolean(Object.values(getValues()).length));
    }
  }, [
    getValues,
    isLoadedForm,
    preloadPersistedFormData,
    setIsFormTouched,
    isDataIncompleteModalOpen,
    setErrorModal,
    guestId,
  ]);

  const goToRemoteKeys = useCallback(() => {
    navigate(getFullPath(PATHS.remoteKeys));
  }, [navigate, getFullPath]);

  return (
    <>
      {isLoadingForm || isGuestLoading ? (
        <LoadingContainer>
          <MainStyledLoader visible width={100} />
          <LoadingText className="kiosko:text-kiosko-m">
            {`${t('loading_form')}...`}
          </LoadingText>
        </LoadingContainer>
      ) : (
        <FormWrapper className="guest-form-wrapper kiosko:gap-14">
          <Fields
            className={cn('guest-form-fields', 'kiosko:flex kiosko:flex-col')}
            $rowsCount={1}
            $hasSignature={hasSignature}
          >
            {renderFormFields()}
          </Fields>
          {SignatureField}
          {FormModals()}
        </FormWrapper>
      )}
      <StyledAutoCompleteGuestDataModalSheet
        isOpenModalSheet={isAutoFillModalOpen}
        handleCloseModal={closeAutoFillModal}
        withCloseButton
        closeButtonType="click"
        className="kiosko:!py-[70px] kiosko:!px-[45px] kiosko:[&_.modal__close-bt]:!top-5 kiosko:[&_.modal__close-bt]:!right-5"
      >
        <AutoFillModalSheet onClose={closeAutoFillModal} />
      </StyledAutoCompleteGuestDataModalSheet>
      <PrintingCardsModal onMaybeLater={goToRemoteKeys} onGoToKeyCards={goToRemoteKeys} />
      <FeedbackModal
        reservationId={reservationId}
        isOpenModalSheet={isFeedbackModalOpen}
        onClose={closeFeedbackModal}
      />
    </>
  );
}

export {GuestFormFields, GuestFormFieldsWrapper};
